import {
  Code2,
  FlaskConical,
  Landmark,
  Languages,
  Palette,
  Sigma,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { LearningTopic } from '@/types/learning-data';

/**
 * One drawn icon per CATEGORY for the phone cards, in a single brand tint.
 * Not the fixtures' per-topic emoji (see the chip comment below for why those
 * stay hidden): six categories in one stroke weight and one colour read as a
 * system, where six emoji read as decoration.
 */
const CATEGORY_ICON: Record<LearningTopic['category'], LucideIcon> = {
  science: FlaskConical,
  math: Sigma,
  language: Languages,
  history: Landmark,
  technology: Code2,
  art: Palette,
};

interface SuggestedTopicsProps {
  topics: LearningTopic[];
  onTopicSelect: (topic: LearningTopic) => void;
  className?: string;
}

/**
 * Glass chip base. The composer directly above these uses
 * `glass-panel edge-lit … bg-card/70 backdrop-blur-sm dark:bg-card/75
 * dark:backdrop-blur-xl`; the chips borrow the same fill and blur one step
 * lighter so they read as small satellites of that panel rather than a second
 * slab of surface. `glass-panel` is the hook for the
 * prefers-reduced-transparency fallback in globals.css.
 *
 * `transition-all` (not the Button base's `transition-colors`) because the
 * hover state moves and scales as well as recolouring — tailwind-merge lets
 * this className win. `overflow-hidden` clips the wash span below to the pill.
 */
const CHIP_CLASS = [
  // `tap-target`: drawn 38px tall, tapped as 44 on a phone; the 10px row gap
  // leaves room for the 3px it adds above and below. No `overflow-hidden`
  // here: it clipped that tap area away, and the hover wash it was guarding
  // is `rounded-full` itself, so nothing needed the clip.
  'tap-target group glass-panel relative h-auto rounded-full',
  'border-border/70 bg-card/70 px-4 py-2 shadow-sm backdrop-blur-sm',
  // Inter 14px/500 in full-strength ink, replacing mono 11px/400 muted:
  //
  //   - SANS, not mono. The workspace type rule is that JetBrains Mono is for
  //     labels, metadata and numerals - never body copy. "Introduction to
  //     Python Programming" is content, not a label, and mono was costing it
  //     both legibility and about 12% extra width per chip at the same size.
  //   - `text-foreground`, not `text-muted-foreground`. Muted 11px mono made
  //     six clickable topics read as disabled captions. The ink is what gives
  //     these presence, which is why the weight does not have to - 500 rather
  //     than the 600 tried first, which was a visible half-step too heavy
  //     across six chips in a row.
  //
  // `text-sm font-medium` are stated explicitly even though the Button base
  // supplies both, because they are a deliberate decision here rather than an
  // inherited default.
  //
  // NOT `text-label`, and this is a trap worth knowing: the custom type scale
  // in tailwind.config.js adds role sizes (`label`, `body`, `heading`, ...)
  // that tailwind-merge's default config has never heard of. It therefore
  // classifies `text-label` as a text-COLOUR, so `text-foreground` alongside
  // it silently evicts it, AND it never conflicts with the base `text-sm` -
  // which survives. A `text-label` here compiled to a 14px chip while reading
  // like a 12px one. Role classes only work through `cn()` once the shared
  // tailwind-merge instance is taught the scale.
  'font-sans text-sm font-medium text-foreground',
  // 300ms rather than the repo's usual 200: the chips lift AND wash, and the
  // faster curve made the wash look like a flicker.
  'transition-all duration-300 ease-out',
  // "Slightly bigger", literally — 4% and a 1px lift. Anything more and a row
  // of six chips visibly jostles its neighbours' baselines on hover.
  'hover:-translate-y-px hover:scale-[1.04]',
  // `hover:bg-card/90` overrides the outline variant's `hover:bg-accent`,
  // which would otherwise flip the chip to an opaque grey and throw away the
  // glass on the one interaction where it matters most.
  'hover:border-brand-300 hover:bg-card/90 hover:text-brand-700',
  'hover:shadow-md hover:shadow-brand-500/20',
  'active:translate-y-0 active:scale-[0.98]',
  // Hairline + a deeper fill in dark, matching the composer. The hover shadow
  // is a brand halo rather than a black lift: black-on-black paints nothing.
  'dark:border-white/[0.09] dark:bg-card/60 dark:shadow-none dark:backdrop-blur-xl',
  'dark:hover:border-primary/40 dark:hover:bg-card/70 dark:hover:text-brand-300',
  'dark:hover:shadow-[0_8px_24px_-12px_hsl(var(--brand-500)/0.45)]',
].join(' ');

/**
 * The colour wash. A brand-tinted gradient that fades in under the label on
 * hover, so the chip warms from within instead of just swapping its border
 * colour. Separate span rather than a `hover:bg-gradient-*` because a gradient
 * cannot cross-fade from `none` — the fill has to already be painted and
 * transition its opacity.
 */
const WASH_CLASS = [
  'pointer-events-none absolute inset-0 rounded-full opacity-0',
  'bg-gradient-to-br from-brand-500/[0.18] via-brand-500/[0.07] to-transparent',
  'transition-opacity duration-300 ease-out group-hover:opacity-100',
  'dark:from-brand-500/25 dark:via-brand-500/10',
].join(' ');

export function SuggestedTopics({
  topics,
  onTopicSelect,
  className = '',
}: SuggestedTopicsProps) {
  if (topics.length === 0) {
    return null;
  }

  return (
    <div className={`mx-auto w-full max-w-4xl ${className}`}>
      <div className='mb-6'>
        {/* Eyebrow register, not a section heading. A `text-lg font-semibold`
            title gave a row of suggestion chips the same weight as the composer
            above it; these are a footnote to the input. The `text-eyebrow`
            role (12px, 0.18em) replaced a hand-rolled `text-[11px]` that sat
            under the repo's 12px floor. */}
        <h3
          id='popular-topics-heading'
          className='mb-3.5 font-mono text-eyebrow uppercase text-muted-foreground'
        >
          Popular learning topics
        </h3>

        <TopicCardRow topics={topics} onTopicSelect={onTopicSelect} />

        <div className='hidden flex-wrap gap-2.5 sm:flex'>
          {topics.map(topic => (
            <Button
              key={topic.id}
              variant='outline'
              className={CHIP_CLASS}
              onClick={() => onTopicSelect(topic)}
            >
              <span aria-hidden className={WASH_CLASS} />
              {/* `topic.icon` is deliberately NOT rendered. The fixtures carry
                  an emoji per topic and it was shown here; six different emoji
                  in a row pulled the eye across the set at random and cost the
                  brand's "sophisticated, not over-gamified" line for no
                  information gain - the title already says what the topic is.
                  `relative` on the label only: it has to sit above the wash,
                  and a z-index on the chip itself would lift it out of the
                  composer's stacking context. */}
              <span className='relative'>{topic.title}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Phone only: the topics as a row of compact tiles you swipe through,
 * snapping one at a time - an icon and the title (two lines at most), in
 * ~70px of height, where the stacked chips spent ~290px on six
 * titles alone. The description is screen-reader text, not a visible line:
 * the taller card that showed it was too much weight under the composer.
 *
 * Tiles are ~64% of the width so the next one always peeks in: that sliver is
 * the only cue the row scrolls, which is why it is not 100%. The row bleeds to
 * the screen edges (`-mx-4 px-4`, matching the page gutter) so a card slides
 * off the edge rather than being clipped at the gutter, and `scroll-px-4`
 * snaps each card back onto the gutter line.
 *
 * Elevation is the border alone, no shadow: one card surface, declared once.
 */
function TopicCardRow({
  topics,
  onTopicSelect,
}: {
  topics: LearningTopic[];
  onTopicSelect: (topic: LearningTopic) => void;
}) {
  return (
    <ul
      aria-labelledby='popular-topics-heading'
      className='-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-1 sm:hidden'
      style={{ scrollbarWidth: 'none' }}
    >
      {topics.map(topic => {
        const Icon = CATEGORY_ICON[topic.category] ?? FlaskConical;
        const detailId = `topic-${topic.id}-detail`;
        return (
          <li key={topic.id} className='w-[64%] shrink-0 snap-start'>
            <button
              type='button'
              onClick={() => onTopicSelect(topic)}
              // Named by its title alone, with the description read after it.
              // The explicit label matters: the screen-reader description is
              // INSIDE the button, so without it the name would be the title
              // and the whole description run together.
              aria-label={topic.title}
              aria-describedby={detailId}
              className='glass-panel flex h-full w-full items-center gap-3 rounded-xl border border-border/70 bg-card/70 p-3 text-left backdrop-blur-sm transition-colors duration-200 active:bg-card/90 dark:border-white/[0.09] dark:bg-card/60 dark:backdrop-blur-xl'
            >
              <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-muted dark:text-primary'>
                <Icon aria-hidden className='h-4 w-4' />
              </span>
              <span className='flex min-w-0 flex-col gap-0.5'>
                <span className='line-clamp-2 text-sm font-semibold leading-snug text-foreground'>
                  {topic.title}
                </span>
                {/* The description is spoken, not shown: on a phone the tile
                    is the title alone, and the copy that made it twice as tall
                    is still there for a screen reader. */}
                <span id={detailId} className='sr-only'>
                  {topic.description}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
